<script lang="ts">
	import { browser } from '$app/env';
	import { goto } from '$app/navigation';
	import { page } from '$app/state';
	import CardNew from '@/components/Cards/CardNew.svelte';
	import { Collections, type ListingsResponse } from '@/pocketbase/pocketbase-types';
	import { currentUser, getErrorMessage, pb } from '@/utils/ApiClient';
	import type { ClientResponseError } from 'pocketbase';
	import { onMount } from 'svelte';
	import { localizeHref } from '@/paraglide/runtime';

	let listingID: string | null;
	let reportMessage: string = '';
	let errorToastVisible: boolean = false;
	let toastMessage: string = '';
	let reportSumbitted: boolean = false;
	let listing: ListingsResponse;

	onMount(async () => {
		listingID = browser ? page.url.searchParams.get('id') : '';
		if (!listingID) {
			browser ? goto('/') : '';
		} else {
			try {
				const response = await pb
					.collection(Collections.Listings)
					.getOne<ListingsResponse>(listingID, { expand: 'boat' });
				listing = response;
			} catch (error) {
				console.error(error);
			}
		}
	});

	function sendReport() {
		if (reportMessage.length < 10) {
			showErrorToast('Por favor, describe el motivo de tu reporte.');
			return;
		}
		pb.collection(Collections.ReportedListings)
			.create({
				listing: listingID,
				reporter: $currentUser?.id,
				reportMessage: reportMessage
			})
			.then((response) => {
				reportSumbitted = true;
			})
			.catch((error: ClientResponseError) => {
				console.error(error);
				showErrorToast(getErrorMessage(error));
			});
	}
	function showErrorToast(message: string) {
		toastMessage = message;
		errorToastVisible = true;
	}
</script>

<div class="mx-auto mb-8 max-w-2xl px-2 pt-0 sm:px-0 sm:pt-8">
	<form>
		<div class="space-y-12">
			<div class="border-b border-gray-900/10 pb-12">
				<h2 class="text-base font-semibold leading-7 text-gray-900">🚩 Reporta un anuncio</h2>
				<p class="mt-1 text-sm leading-6 text-gray-600">
					Por favor, describe las razones por la que estas reportando este anuncio. El equipo de
					KokiBoats lo revisara lo antes possible.
				</p>
				{#if listing}
					<h2 class="my-4 text-base font-semibold leading-7 text-gray-900">
						Anuncio que estas reportando
					</h2>
					<div
						class="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-2 lg:gap-5 xl:grid-cols-2 2xl:grid-cols-4"
					>
						<CardNew listing={listing} />
					</div>
				{/if}
				{#if !reportSumbitted}
					<div class="mt-4 grid grid-cols-1 gap-x-6 gap-y-8 pt-4 sm:grid-cols-6">
						<div class="col-span-full">
							<label for="about" class="block text-sm font-medium leading-6 text-gray-900"
								>Descripcion del reporte</label
							>
							<div class="mt-2">
								<textarea
									bind:value={reportMessage}
									id="report"
									name="report"
									rows="3"
									class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
								></textarea>
							</div>
						</div>
					</div>{:else}
					<div class="mx-2 mt-10 rounded-md bg-green-50 p-4 sm:mx-0">
						<div class="flex">
							<div class="shrink-0">
								<svg
									class="h-5 w-5 text-green-400"
									viewBox="0 0 20 20"
									fill="currentColor"
									aria-hidden="true"
								>
									<path
										fill-rule="evenodd"
										d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
										clip-rule="evenodd"
									></path>
								</svg>
							</div>
							<div class="ml-3">
								<h3 class="text-sm font-medium text-green-800">Reporte enviado</h3>
								<div class="mt-2 text-sm text-green-700">
									<p>El equipo de KokiBoats lo revisara. Gracias por contribuir a la comunidad.</p>
									<p>
										Encuentra otros barcos <a href={localizeHref('/')} class="mx-4 font-bold"
											>Buscar</a
										>
									</p>
								</div>
							</div>
						</div>
					</div>
				{/if}
				{#if errorToastVisible}
					<div class="mt-8 rounded-md bg-red-50 p-4">
						<div class="flex">
							<div class="shrink-0">
								<svg
									class="h-5 w-5 text-red-400"
									viewBox="0 0 20 20"
									fill="currentColor"
									aria-hidden="true"
								>
									<path
										fill-rule="evenodd"
										d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
										clip-rule="evenodd"
									></path>
								</svg>
							</div>
							<div class="ml-3">
								<p class="text-sm font-medium text-red-800">{toastMessage}</p>
							</div>
							<div class="ml-auto pl-3">
								<div class="-mx-1.5 -my-1.5">
									<button
										on:click={() => {
											errorToastVisible = false;
										}}
										type="button"
										class="inline-flex rounded-md bg-red-50 p-1.5 text-red-500 hover:bg-red-100 focus:outline-hidden focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-green-50"
									>
										<span class="sr-only">Dismiss</span>
										<svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
											<path
												d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"
											></path>
										</svg>
									</button>
								</div>
							</div>
						</div>
					</div>
				{/if}
			</div>
		</div>
		{#if !reportSumbitted}
			<div class="mt-6 flex items-center justify-end gap-x-6">
				<button
					on:click={sendReport}
					type="submit"
					class="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
					>Enviar</button
				>
			</div>
		{/if}
	</form>
</div>
